// ============================================================================
// OBT — Backend enum değerleri ve Türkçe kullanıcı etiketleri
// ============================================================================
// KAYNAK: backend/modules/obt/enums.py
// Buradaki teknik değerler backend'de GERÇEKTEN üretilen değerlerdir; yeni
// status icat edilmez. Kullanıcıya asla teknik değer gösterilmez — daima
// `obtLabel(map, value)` üzerinden Türkçe karşılığı gösterilir.
//
// Bilinmeyen bir değer gelirse (backend ileride yeni bir durum eklerse) UI
// kırılmaz: değer olduğu gibi ama nötr bir görünümle gösterilir.
// ============================================================================

/** enums.py :: CampaignStatus */
export const CAMPAIGN_STATUS_LABELS = Object.freeze({
  draft: 'Taslak',
  published: 'Yayınlandı',
  collecting_responses: 'Cevap Toplanıyor',
  under_review: 'İncelemede',
  director_review: 'Müdür İncelemesinde',
  decided: 'Karara Bağlandı',
  completed: 'Tamamlandı',
  postponed: 'Ertelendi',
  cancelled: 'İptal Edildi',
});

/** enums.py :: ResponseStatus */
export const RESPONSE_STATUS_LABELS = Object.freeze({
  draft: 'Taslak',
  submitted: 'Gönderildi',
  revision_requested: 'Revizyon İstendi',
  resubmitted: 'Yeniden Gönderildi',
});

/**
 * enums.py :: ReviewState
 * NOT: `null` = gönderildi ancak BM incelemesi henüz başlamadı.
 */
export const REVIEW_STATE_LABELS = Object.freeze({
  bm_review: 'BM İncelemesinde',
  bm_revision_requested: 'BM Revizyon İstedi',
  bm_approved: 'BM Onayladı',
  bm_rejected: 'BM Reddetti',
  my_review: 'MY İncelemesinde',
  my_revision_requested: 'MY Revizyon İstedi',
  my_approved: 'MY Onayladı',
  director_review: 'Müdür İncelemesinde',
  director_selected: 'Müdür Seçti',
  director_rejected: 'Müdür Reddetti',
  director_postponed: 'Müdür Erteledi',
  completed: 'Tamamlandı',
});

/** enums.py :: SuggestedParkStatus */
export const SUGGESTED_PARK_STATUS_LABELS = Object.freeze({
  pending_bm_review: 'BM Onayı Bekliyor',
  accepted: 'Kabul Edildi',
  rejected: 'Reddedildi',
});

/** enums.py :: DecisionSetStatus */
export const DECISION_SET_STATUS_LABELS = Object.freeze({
  draft: 'Taslak',
  finalized: 'Kesinleşti',
  superseded: 'Yerine Yenisi Geçti',
});

/** enums.py :: ApplicationResultStatus */
export const APPLICATION_RESULT_STATUS_LABELS = Object.freeze({
  planned: 'Planlandı',
  completed: 'Tamamlandı',
  postponed: 'Ertelendi',
  cancelled: 'İptal Edildi',
  revised: 'Revize Edildi',
});

/** enums.py :: QuestionType — F2 dinamik soru tasarımcısı için hazır. */
export const QUESTION_TYPE_LABELS = Object.freeze({
  boolean: 'Evet / Hayır',
  text: 'Serbest Metin',
  single_select: 'Tek Seçim',
  multi_select: 'Çoklu Seçim',
  photo: 'Fotoğraf',
  document: 'Belge',
});

/**
 * Durum → görsel ton eşlemesi.
 * Renk TEK BAŞINA durum göstergesi değildir; StatusBadge daima metin de
 * gösterir (erişilebilirlik gereği).
 */
export const OBT_STATUS_TONES = Object.freeze({
  // nötr / devam eden
  draft: 'neutral',
  published: 'info',
  collecting_responses: 'info',
  submitted: 'info',
  resubmitted: 'info',
  under_review: 'warning',
  director_review: 'warning',
  bm_review: 'warning',
  my_review: 'warning',
  pending_bm_review: 'warning',
  revision_requested: 'warning',
  bm_revision_requested: 'warning',
  my_revision_requested: 'warning',
  postponed: 'warning',
  director_postponed: 'warning',
  planned: 'info',
  // olumlu
  bm_approved: 'success',
  my_approved: 'success',
  director_selected: 'success',
  accepted: 'success',
  decided: 'success',
  completed: 'success',
  finalized: 'success',
  // olumsuz
  bm_rejected: 'danger',
  director_rejected: 'danger',
  rejected: 'danger',
  cancelled: 'danger',
  // arşiv
  superseded: 'neutral',
  revised: 'neutral',
});

/**
 * Teknik enum değerini kullanıcı metnine çevirir.
 * @param {Record<string,string>} labelMap
 * @param {string|null|undefined} value
 * @param {string} fallback `value` boşsa gösterilecek metin
 */
export function obtLabel(labelMap, value, fallback = 'Belirsiz') {
  if (value === null || value === undefined || value === '') return fallback;
  return labelMap[value] || String(value);
}

/** Durum değerinin görsel tonu; bilinmeyen değer nötr kabul edilir. */
export function obtTone(value) {
  if (!value) return 'neutral';
  return OBT_STATUS_TONES[value] || 'neutral';
}

/**
 * Kampanya AŞAMASI — ham `status` yerine sayaçlardan türetilmiş, kullanıcı-dostu
 * etiket. Sorun: kampanya cevaplar toplanıp incelemeye/karara geçse de backend
 * durumu `collecting_responses`'ta kalıyor → kart "Cevap Toplanıyor" gösterip
 * "hiç ilerlemedi" gibi görünüyordu. Burada gerçek ilerleme yansıtılır.
 * @returns {{label:string, tone:string}}
 */
export function campaignPhase(campaign) {
  const status = campaign?.status;
  if (status === 'draft') return { label: 'Taslak', tone: 'neutral' };
  if (status === 'cancelled') return { label: 'İptal Edildi', tone: 'danger' };
  if (status === 'decided' || status === 'completed') {
    return { label: 'Karara bağlandı', tone: 'success' };
  }
  const answered = campaign?.submitted_response_count ?? 0;
  const pendingDir = campaign?.pending_director_count ?? 0;
  const pendingReview =
    (campaign?.pending_bm_count ?? 0) + (campaign?.pending_my_count ?? 0);
  const missing = campaign?.no_response_person_count ?? 0;
  if (pendingDir > 0) return { label: 'Müdür kararı bekliyor', tone: 'warning' };
  if (pendingReview > 0) return { label: 'İncelemede', tone: 'warning' };
  if (answered > 0) {
    return missing === 0
      ? { label: 'Cevaplar toplandı', tone: 'success' }
      : { label: `Cevaplar geliyor · ${answered}`, tone: 'info' };
  }
  return { label: 'Cevap bekleniyor', tone: 'info' };
}
